import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type FC
} from 'react'
import { useTranslation } from 'i18n'
import { OnboardingV3Context } from 'context/onboarding-v3'
import { Cdn } from 'utils/assets'
import { useOnboardingChecklist } from 'hooks/use-onboarding-checklist'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { getVisibleChecklists } from 'utils/features-control/features-control'
import OnboardingChecklistFull from '../onboarding-checklist-full'
import * as S from './FloatingOnboardingButton.styles'

const primeirosPassosIcon = Cdn.getImageUrl(
  'onboarding/primeiros-passos-icon.svg'
)

const POLLING_INTERVAL = 30000

const FloatingOnboardingButton: FC = () => {
  const { state, shouldShowOnboarding, openModalCount } =
    useContext(OnboardingV3Context)
  const { shouldShowChecklist, checklistStatus, refetch } =
    useOnboardingChecklist()
  const { user } = useUserInfo()
  const { t } = useTranslation()
  const isAnyModalOpen = openModalCount > 0

  const hasStoryTreeFlag = user?.featureToggles?.storyTree ?? false

  const visibleChecklists = useMemo(() => getVisibleChecklists(user), [user])
  const [isChecklistOpen, setIsChecklistOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)

  useEffect(() => {
    if (isAnyModalOpen && isChecklistOpen) {
      setIsChecklistOpen(false)
    }
  }, [isAnyModalOpen, isChecklistOpen])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  const [position, setPosition] = useState({ x: 24, y: 24 })
  const [isDragging, setIsDragging] = useState(false)
  const [isMouseDown, setIsMouseDown] = useState(false)
  const [isVibrating, setIsVibrating] = useState(false)
  const [cursorStyle, setCursorStyle] = useState<'pointer' | 'default'>(
    'default'
  )
  const [checklistAlignment, setChecklistAlignment] = useState<
    'left' | 'right'
  >('left')
  const previousChecklistRef = useRef<{
    reading: boolean
    conquests: boolean
    tree: boolean
  } | null>(null)
  const dragRef = useRef<{
    startX: number
    startY: number
    initialX: number
    initialY: number
  } | null>(null)

  const getMaxYPosition = useCallback(() => {
    if (window.innerWidth <= 768) {
      return window.innerHeight - 100
    }
    const buttonHeight = 44
    const gap = 100
    const topPadding = 100
    const minY = 250
    const checklistMaxHeight = window.innerWidth <= 1024 ? 400 : 447
    const totalNeededHeight =
      checklistMaxHeight + buttonHeight + gap + topPadding
    const maxY = window.innerHeight - totalNeededHeight
    return Math.max(minY, maxY)
  }, [])

  const getButtonWidth = useCallback(() => {
    if (window.innerWidth <= 768) {
      return 48
    }
    return 200
  }, [])

  const getChecklistWidth = useCallback(() => {
    if (window.innerWidth <= 768) {
      return 0
    }
    return window.innerWidth <= 1024 ? 400 : 447
  }, [])

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragRef.current) return

      const deltaX = e.clientX - dragRef.current.startX
      const deltaY = dragRef.current.startY - e.clientY

      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        setIsDragging(true)
      }

      const maxY =
        isChecklistOpen && window.innerWidth > 768
          ? getMaxYPosition()
          : window.innerHeight - 100
      const minY = window.innerWidth > 768 ? 40 : 20
      const buttonWidth = getButtonWidth()
      const checklistWidth = getChecklistWidth()

      let maxX: number
      if (window.innerWidth > 768) {
        if (isChecklistOpen) {
          const screenCenter = window.innerWidth / 2
          const buttonCenterX =
            dragRef.current.initialX + deltaX + buttonWidth / 2
          const alignment = buttonCenterX < screenCenter ? 'left' : 'right'

          if (alignment === 'right') {
            maxX = window.innerWidth - buttonWidth - 24
          } else {
            maxX = window.innerWidth - checklistWidth - 24
          }
        } else {
          maxX = window.innerWidth - buttonWidth - 24
        }
      } else {
        maxX = window.innerWidth - buttonWidth - 24
      }
      const newX = Math.max(
        24,
        Math.min(maxX, dragRef.current.initialX + deltaX)
      )
      let newY = dragRef.current.initialY + deltaY

      if (newY < minY) {
        newY = minY
      } else if (newY > maxY) {
        newY = maxY
      }

      setPosition({ x: newX, y: newY })

      if (isChecklistOpen && window.innerWidth > 768) {
        const screenCenter = window.innerWidth / 2
        const buttonCenterX = newX + getButtonWidth() / 2
        const newAlignment = buttonCenterX < screenCenter ? 'left' : 'right'
        setChecklistAlignment(prev => {
          if (prev !== newAlignment) {
            return newAlignment
          }
          return prev
        })
      }
    },
    [isChecklistOpen, getMaxYPosition, getButtonWidth, getChecklistWidth]
  )

  const handleMouseUp = useCallback(() => {
    setIsMouseDown(false)
    dragRef.current = null
    setTimeout(() => setIsDragging(false), 100)
  }, [])

  useEffect(() => {
    if (isMouseDown) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)

      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isMouseDown, handleMouseMove, handleMouseUp])

  useEffect(() => {
    if (isChecklistOpen && !isDragging && !isMouseDown) {
      const maxY = getMaxYPosition()
      setPosition(prev => {
        const updatedPosition = {
          x: prev.x,
          y: prev.y > maxY ? maxY : prev.y
        }

        if (window.innerWidth > 768) {
          const screenCenter = window.innerWidth / 2
          const buttonCenterX = updatedPosition.x + getButtonWidth() / 2
          const newAlignment = buttonCenterX < screenCenter ? 'left' : 'right'
          setChecklistAlignment(newAlignment)
        }

        return updatedPosition
      })
    }
  }, [
    isChecklistOpen,
    getMaxYPosition,
    isDragging,
    isMouseDown,
    getButtonWidth
  ])

  useEffect(() => {
    if (isChecklistOpen && window.innerWidth > 768) {
      const screenCenter = window.innerWidth / 2
      const buttonCenterX = position.x + getButtonWidth() / 2
      const newAlignment = buttonCenterX < screenCenter ? 'left' : 'right'
      setChecklistAlignment(newAlignment)
    }
  }, [isChecklistOpen, position.x, getButtonWidth])

  useEffect(() => {
    const handleResize = () => {
      if (isChecklistOpen && window.innerWidth > 768) {
        const screenCenter = window.innerWidth / 2
        const buttonCenterX = position.x + getButtonWidth() / 2
        const newAlignment = buttonCenterX < screenCenter ? 'left' : 'right'
        setChecklistAlignment(newAlignment)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isChecklistOpen, position.x, getButtonWidth])

  useEffect(() => {
    if (!checklistStatus) return

    const currentItems: {
      reading: boolean
      conquests: boolean
      tree: boolean
    } = {
      reading: checklistStatus.firstReading,
      conquests: visibleChecklists.showConquests
        ? checklistStatus.achievementArea
        : true,
      tree: visibleChecklists.showTree ? checklistStatus.plantTree : true
    }

    const previousStatus = previousChecklistRef.current

    if (previousStatus) {
      const previousPending = Object.values(previousStatus).filter(
        completed => !completed
      ).length
      const currentPending = Object.values(currentItems).filter(
        completed => !completed
      ).length

      if (currentPending < previousPending) {
        setIsVibrating(true)
        setTimeout(() => setIsVibrating(false), 800)
      }
    }

    previousChecklistRef.current = currentItems
  }, [checklistStatus, visibleChecklists])

  useEffect(() => {
    if (!shouldShowChecklist || isChecklistOpen) return

    const intervalId = setInterval(async () => {
      try {
        await refetch()
      } catch {
        return
      }
    }, POLLING_INTERVAL)

    return () => clearInterval(intervalId)
  }, [shouldShowChecklist, isChecklistOpen, refetch])

  const handleMouseDown = (e: React.MouseEvent) => {
    if (window.innerWidth <= 768) return

    setIsDragging(false)
    setIsMouseDown(true)
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    setIsDragging(false)
    setIsMouseDown(true)
    dragRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      initialX: position.x,
      initialY: position.y
    }
  }

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!dragRef.current || !isMouseDown) return

      e.preventDefault()
      const touch = e.touches[0]
      const deltaX = touch.clientX - dragRef.current.startX
      const deltaY = dragRef.current.startY - touch.clientY

      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        setIsDragging(true)
      }

      const maxY =
        isChecklistOpen && window.innerWidth > 768
          ? getMaxYPosition()
          : window.innerHeight - 100
      const minY = window.innerWidth > 768 ? 40 : 20
      const buttonWidth = getButtonWidth()
      const checklistWidth = getChecklistWidth()

      let maxX: number
      if (window.innerWidth > 768) {
        if (isChecklistOpen) {
          const screenCenter = window.innerWidth / 2
          const buttonCenterX =
            dragRef.current.initialX + deltaX + buttonWidth / 2
          const alignment = buttonCenterX < screenCenter ? 'left' : 'right'

          if (alignment === 'right') {
            maxX = window.innerWidth - buttonWidth - 24
          } else {
            maxX = window.innerWidth - checklistWidth - 24
          }
        } else {
          maxX = window.innerWidth - buttonWidth - 24
        }
      } else {
        maxX = window.innerWidth - buttonWidth - 24
      }
      const newX = Math.max(
        24,
        Math.min(maxX, dragRef.current.initialX + deltaX)
      )
      let newY = dragRef.current.initialY + deltaY

      if (newY < minY) {
        newY = minY
      } else if (newY > maxY) {
        newY = maxY
      }

      setPosition({ x: newX, y: newY })

      if (isChecklistOpen && window.innerWidth > 768) {
        const screenCenter = window.innerWidth / 2
        const buttonCenterX = newX + getButtonWidth() / 2
        const newAlignment = buttonCenterX < screenCenter ? 'left' : 'right'
        setChecklistAlignment(prev => {
          if (prev !== newAlignment) {
            return newAlignment
          }
          return prev
        })
      }
    },
    [
      isChecklistOpen,
      getMaxYPosition,
      getButtonWidth,
      getChecklistWidth,
      isMouseDown
    ]
  )

  const handleTouchEnd = useCallback(() => {
    setIsMouseDown(false)
    dragRef.current = null
    setTimeout(() => setIsDragging(false), 100)
  }, [])

  useEffect(() => {
    if (!isMouseDown) return

    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('touchend', handleTouchEnd)

    return () => {
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [isMouseDown, handleTouchMove, handleTouchEnd])

  const checklistItems = useMemo(() => {
    const base = {
      interests: true,
      reading: checklistStatus?.firstReading ?? false
    }

    const items: {
      interests: boolean
      reading: boolean
      conquests?: boolean
      tree?: boolean
    } = { ...base }

    if (visibleChecklists.showConquests) {
      items.conquests = checklistStatus?.achievementArea ?? false
    }

    if (visibleChecklists.showTree) {
      items.tree = checklistStatus?.plantTree ?? false
    }

    return items
  }, [checklistStatus, visibleChecklists])

  const pendingCount = useMemo(() => {
    return Object.values(checklistItems).filter(completed => !completed).length
  }, [checklistItems])

  if (
    hasStoryTreeFlag ||
    !shouldShowOnboarding ||
    !state.showFloatingButton ||
    !shouldShowChecklist
  ) {
    return null
  }

  const handleToggleChecklist = async () => {
    if (!isDragging) {
      const newState = !isChecklistOpen
      setIsChecklistOpen(newState)
      setCursorStyle('default')

      if (newState) {
        await refetch()

        const maxY = getMaxYPosition()
        setPosition(prev => {
          const updatedPosition = {
            x: prev.x,
            y: prev.y > maxY ? maxY : prev.y
          }

          if (window.innerWidth > 768) {
            const screenCenter = window.innerWidth / 2
            const buttonCenterX = updatedPosition.x + getButtonWidth() / 2
            const newAlignment = buttonCenterX < screenCenter ? 'left' : 'right'
            setChecklistAlignment(newAlignment)
          }

          return updatedPosition
        })
      }
    }
  }

  return (
    <>
      <S.Backdrop
        $isOpen={isChecklistOpen}
        onClick={() => setIsChecklistOpen(false)}
      />
      <S.FloatingButtonContainer
        $x={position.x}
        $y={position.y}
        $isDragging={isDragging}
        $isChecklistOpen={isChecklistOpen}
        $isModalOpen={isAnyModalOpen}
        $isMobile={isMobile}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <S.ChecklistContainer
          $isOpen={isChecklistOpen}
          $alignment={checklistAlignment}
          $buttonWidth={getButtonWidth()}
          $checklistWidth={getChecklistWidth()}
        >
          <OnboardingChecklistFull onClose={() => setIsChecklistOpen(false)} />
        </S.ChecklistContainer>

        <S.FloatingButton
          $isHovered={isHovered}
          $isVibrating={isVibrating}
          $isDragging={isDragging}
          $cursorStyle={cursorStyle}
          onClick={handleToggleChecklist}
          onMouseEnter={() => {
            if (!isDragging) {
              setIsHovered(true)
              setCursorStyle('pointer')
            }
          }}
          onMouseLeave={() => {
            if (!isDragging) {
              setIsHovered(false)
              setCursorStyle('default')
            }
          }}
          aria-label={t('Primeiros passos')}
        >
          <S.ButtonIcon src={primeirosPassosIcon} alt="" />
          <S.ButtonText>{t('Primeiros passos')}</S.ButtonText>
          {pendingCount > 0 && <S.Badge>{pendingCount}</S.Badge>}
        </S.FloatingButton>
      </S.FloatingButtonContainer>
    </>
  )
}

export default FloatingOnboardingButton
